import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Lavorazione, Risorsa, RisorsaEsolver, RisorsaLavorazione, RisorsaMtConnect, RisorsaTracciato, RisorsaZund, SearchResult} from '../_models/_Index';
import {environment} from '../../environments/environment';
import {BehaviorSubject, firstValueFrom, Subject} from 'rxjs';
import {HubConnection} from '@microsoft/signalr';
import {buildHubConnection, startHubConnection, stopHubConnection} from '../_helpers/hub-connection.helper';
import {HubState} from '../_models/hub-state';
import {OrganizzazioniService} from "./organizzazioni.service";
import {SaveMessage} from '../_helpers/save-message';
import {RisorsaChild} from "../_models/RisorsaChild";
import {TipoRisorsa} from '../_models/generated/tipo-risorsa'
import {AuthenticationService} from './authentication.service';
import {STATI_SCRITTURA_DEFAULT} from '../_helpers/stati-scrittura';

const cachePrefix = 'archiconnect-v2-';

const emptyRisorsa: Risorsa = {
  reparto: '',
  organizzazioneId: 0,
  formato: '',
  indirizzoIp: '',
  id: 0,
  codice: '',
  descrizione: '',
  isActive: true,
  continuousLaunch: false,
  statiScrittura: STATI_SCRITTURA_DEFAULT,
  continuousSend: false,
  condition: 0,
  avanzaMode: 0,
  tipoRisorsa: TipoRisorsa.TUTTO
};

@Injectable({providedIn: 'root'})
export class RisorseService {
  public lastRisorsaCreated: BehaviorSubject<Risorsa> = new BehaviorSubject<Risorsa>(null);
  public lastRisorsaUpdated: BehaviorSubject<Risorsa> = new BehaviorSubject<Risorsa>(null);
  public lastRisorsaDeleted: BehaviorSubject<Risorsa> = new BehaviorSubject<Risorsa>(null);
  public hubState: BehaviorSubject<number> = new BehaviorSubject<number>(HubState.Connecting);
  public handleSave: Subject<number> = new Subject();
  public searchRisorsa: BehaviorSubject<Risorsa> = new BehaviorSubject(emptyRisorsa);
  public myRisorse: BehaviorSubject<Risorsa[]> = new BehaviorSubject([]);
  private hubConnection: HubConnection;

  constructor(
    private authService: AuthenticationService,
    private organizzazioniService: OrganizzazioniService,
    private http: HttpClient,
    private saveMessage: SaveMessage) {
    const risorsaJson = localStorage.getItem(cachePrefix + 'risorsa');
    if (risorsaJson !== undefined && risorsaJson !== 'undefined') {
      const risorsa = JSON.parse(risorsaJson) || emptyRisorsa;
      this.searchRisorsa.next(risorsa);
    }
    // Se SignalR notifica l'aggiornamento della risorsa in cache, riallinea localStorage + searchRisorsa.
    this.lastRisorsaUpdated.subscribe((risorsa: Risorsa) => {
      if (risorsa && this.searchRisorsa.value && this.searchRisorsa.value.id === risorsa.id) {
        this.setRisorsa(risorsa);
      }
    });
  }

  public startConnection() {
    this.hubConnection = buildHubConnection(`${environment.localApiUrl}hubs/risorse`, this.hubState);

    this.hubConnection.on('SendCreated', (risorsa: Risorsa) => {
      if (risorsa.organizzazioneId === this.organizzazioniService.organizzazioneId) {
        this.saveMessage.entityCreated('risorsa', risorsa);
        this.lastRisorsaCreated.next(risorsa);
      }
    });

    this.hubConnection.on('SendUpdated', (risorsa: Risorsa) => {
      if (risorsa.organizzazioneId === this.organizzazioniService.organizzazioneId) {
        // La sincronizzazione della cache è gestita dalla subscription a lastRisorsaUpdated nel costruttore.
        this.lastRisorsaUpdated.next(risorsa);
      }
    });

    this.hubConnection.on('SendDeleted', (risorsa: Risorsa) => {
      if (risorsa.organizzazioneId === this.organizzazioniService.organizzazioneId) {
        this.saveMessage.entityDeleted('risorsa', risorsa);
        this.lastRisorsaDeleted.next(risorsa);
      }
    });

    return startHubConnection(this.hubConnection, this.hubState);
  }

  public stopConnection() {
    void stopHubConnection(this.hubConnection);
  }

  setRisorsa(risorsa: Risorsa) {
    if (risorsa !== undefined) {
      this.searchRisorsa.next(risorsa);
      localStorage.setItem(cachePrefix + 'risorsa', JSON.stringify(risorsa));
    }
  }

  resetRisorsa() {
    this.searchRisorsa.next(emptyRisorsa);
    localStorage.setItem(cachePrefix + 'risorsa', JSON.stringify(emptyRisorsa));
  }

  async getMyRisorseFilteredAsync(orgId: number, search: string, page: number, count: number, sortField: string, sortAsc: boolean, tipo: number, reparto: string, excludeTutto: boolean, excludeInactive: boolean) {
    return firstValueFrom(
      this.http.post<SearchResult<Risorsa>>(`${environment.localApiUrl}api/Risorse/MyRisorseFiltered?orgId=${orgId}`,
        {search, page, count, sortField, sortAsc, tipo, reparto, excludeTutto, excludeInactive})
    );
  }

  async getRisorseFilteredAsync(orgId: number, search: string, page: number, count: number, sortField: string, sortAsc: boolean, tipo: number, reparto: string, excludeTutto: boolean, excludeInactive: boolean) {
    return firstValueFrom(this.http.post<SearchResult<Risorsa>>(`${environment.localApiUrl}api/Risorse/RisorseFiltered?orgId=${orgId}`,
      {search, page, count, sortField, sortAsc, tipo, reparto, excludeTutto, excludeInactive}));
  }

  async getRisorsaAsync(orgId: number, code: string) {
    return firstValueFrom(this.http.get<Risorsa>(`${environment.localApiUrl}api/Risorse/Risorsa?orgId=${orgId}&code=${code}`));
  }

  async getRisorsaFromIdAsync(orgId: number, risorsaId: number) {
    return firstValueFrom(this.http.get<Risorsa>(`${environment.localApiUrl}api/Risorse/RisorsaFromId?orgId=${orgId}&risorsaId=${risorsaId}`));
  }

  async createRisorsaAsync(risorsa: Risorsa) {
    risorsa.executedBy ??= this.getNotificationActor();
    return firstValueFrom(this.http.post<Risorsa>(`${environment.localApiUrl}api/Risorse/CreateRisorsa`, risorsa));
  }

  async updateRisorsaAsync(risorsa: Risorsa) {
    risorsa.executedBy ??= this.getNotificationActor();
    return firstValueFrom(this.http.post<Risorsa>(`${environment.localApiUrl}api/Risorse/UpdateRisorsa`, risorsa));
  }

  async deleteRisorsaAsync(orgId: number, code: string) {
    const executedBy = this.getNotificationActor();
    const actorQuery = executedBy ? `&executedBy=${encodeURIComponent(executedBy)}` : '';
    return firstValueFrom(this.http.get<{ result: string }>(`${environment.localApiUrl}api/Risorse/DeleteRisorsa?orgId=${orgId}&code=${code}${actorQuery}`));
  }

  async getRisorseOfAccountAsync(orgId: number, accountId: number) {
    return firstValueFrom(this.http.get<SearchResult<Risorsa>>(`${environment.localApiUrl}api/Risorse/RisorseByAccountId?` +
      `orgId=${orgId}&accountId=${accountId}`));
  }

  setMyRisorse(orgId: number) {
    firstValueFrom(
      this.http.get<SearchResult<Risorsa>>(`${environment.localApiUrl}api/Risorse/MyRisorse?orgId=${orgId}`)
    ).then((_risorse: SearchResult<Risorsa>) => this.myRisorse.next(_risorse.result));
  }

  async addRisorseToAccountByRepartoAsync(accountId: number, orgId: number, reparto: string, alsoLavorazioni: boolean) {
    return firstValueFrom(this.http
      .post<any>(`${environment.localApiUrl}api/Risorse/AddRisorseToAccountByReparto`,
        {accountId, orgId, reparto, alsoLavorazioni}));
  }

  async addRisorsaToAccountAsync(accountId: number, orgId: number, risorsaId: number, alsoLavorazioni: boolean) {
    return firstValueFrom(this.http
      .post<Risorsa>(`${environment.localApiUrl}api/Risorse/AddRisorsaToAccount`,
        {accountId, orgId, risorsaId, alsoLavorazioni}));
  }

  async removeRisorsaFromAccountAsync(accountId: number, orgId: number, risorsaId: number) {
    return firstValueFrom(this.http
      .get<Risorsa>(`${environment.localApiUrl}api/Risorse/RemoveRisorsaFromAccount?` +
        `accountId=${accountId}&orgId=${orgId}&risorsaId=${risorsaId}`));
  }

  async getTracciatoOfRisorsaAsync(risorsaId: number, tracciatoId: number, typeId: number, risorsaTracciatoId: number) {
    return firstValueFrom(this.http.get<RisorsaTracciato>(`${environment.localApiUrl}api/Risorse/TracciatoOfRisorsa?` +
      `risorsaId=${risorsaId}&tracciatoId=${tracciatoId}&typeId=${typeId}&risorsaTracciatoId=${risorsaTracciatoId}`));
  }

  async updateTracciatoOfRisorsaAsync(risorsaTracciato: RisorsaTracciato) {
    return firstValueFrom(this.http.post<RisorsaTracciato>(`${environment.localApiUrl}api/Risorse/UpdateTracciatoOfRisorsa`, risorsaTracciato));
  }

  async getTracciatiOfRisorsaAsync(orgId: number, code: string) {
    return firstValueFrom(this.http.get<SearchResult<RisorsaTracciato>>(`${environment.localApiUrl}api/Risorse/TracciatiOfRisorsa?` +
      `orgId=${orgId}&code=${code}`));
  }

  async addTracciatoToRisorsaAsync(risorsaTracciato: RisorsaTracciato) {
    return firstValueFrom(this.http.post<RisorsaTracciato>(`${environment.localApiUrl}api/Risorse/AddTracciatoToRisorsa`, risorsaTracciato));
  }

  async removeTracciatoFromRisorsaAsync(risorsaId: number, tracciatoId: number, tracciatoType: number, risorsaTracciatoId: number) {
    return firstValueFrom(this.http.get<Risorsa>(`${environment.localApiUrl}api/Risorse/RemoveTracciatoFromRisorsa?` +
      `risorsaId=${risorsaId}&tracciatoId=${tracciatoId}&tracciatoType=${tracciatoType}&risorsaTracciatoId=${risorsaTracciatoId}`));
  }

  async createRisorseLavorazioni(risorsaLavorazione: RisorsaLavorazione) {
    return firstValueFrom(this.http.post<RisorsaLavorazione>(`${environment.localApiUrl}api/Risorse/CreateRisorseLavorazioni`, risorsaLavorazione));
  }

  async deleteRisorseLavorazioni(idRisorsa: number, idLavorazione: number) {
    return firstValueFrom(this.http.get<RisorsaLavorazione>(`${environment.localApiUrl}api/Risorse/DeleteRisorseLavorazioni?idRisorsa=${idRisorsa}&idLavorazione=${idLavorazione}`));
  }

  async getLavorazioniRisorsaAsync(orgId: number, code: string) {
    return firstValueFrom(this.http.get<SearchResult<Lavorazione>>(`${environment.localApiUrl}api/Lavorazioni/GetLavorazioniOfRisorsa?orgId=${orgId}&code=${code}`));
  }

  async getChildsRisorsaAsync(orgId: number, risorsaCode: string, search: string, page: number, count: number) {
    return firstValueFrom(this.http.post<SearchResult<Risorsa>>(`${environment.localApiUrl}api/Risorse/ChildsOfRisorsa?orgId=${orgId}`,
      {risorsaCode, search, page, count}));
  }

  async addChildToRisorsaAsync(risorsaChild: RisorsaChild) {
    return firstValueFrom(this.http.post<RisorsaChild>(`${environment.localApiUrl}api/Risorse/CreateRisorsaChild`, risorsaChild));
  }

  async removeChildFromRisorsaAsync(risorsaId: number, childId: number) {
    return firstValueFrom(this.http.get<RisorsaChild>(`${environment.localApiUrl}api/Risorse/DeleteRisorsaChild?risorsaId=${risorsaId}&childId=${childId}`));
  }

  async getRisorsaZundAsync(risorseId: number) {
    return firstValueFrom(this.http.get<RisorsaZund>(`${environment.localApiUrl}api/Risorse/RisorsaZund?risorseId=${risorseId}`));
  }

  async createRisorsaZundAsync(risorsaZund: RisorsaZund) {
    return firstValueFrom(this.http.post<RisorsaZund>(`${environment.localApiUrl}api/Risorse/CreateRisorsaZund`, risorsaZund));
  }

  async updateRisorsaZundAsync(risorsaZund: RisorsaZund) {
    return firstValueFrom(this.http.post<RisorsaZund>(`${environment.localApiUrl}api/Risorse/UpdateRisorsaZund`, risorsaZund));
  }

  async getRisorsaMtConnectAsync(risorseId: number) {
    return firstValueFrom(this.http.get<RisorsaMtConnect>(`${environment.localApiUrl}api/Risorse/RisorsaMtConnect?risorseId=${risorseId}`));
  }

  async createRisorsaMtConnectAsync(risorsaMtConnect: RisorsaMtConnect) {
    return firstValueFrom(this.http.post<RisorsaMtConnect>(`${environment.localApiUrl}api/Risorse/CreateRisorsaMtConnect`, risorsaMtConnect));
  }

  async updateRisorsaMtConnectAsync(risorsaMtConnect: RisorsaMtConnect) {
    return firstValueFrom(this.http.post<RisorsaMtConnect>(`${environment.localApiUrl}api/Risorse/UpdateRisorsaMtConnect`, risorsaMtConnect));
  }

  async getRisorseEsolver(orgId: number) {
    return firstValueFrom(this.http.get<SearchResult<RisorsaEsolver>>(`${environment.localApiUrl}api/Esolver/Risorse/${orgId}`));
  }

  private getNotificationActor(): string | undefined {
    return this.organizzazioniService.codiceOperatore ?? this.authService.currentUserValue?.userName;
  }
}
